import { ReadonlyVec2, ReadonlyVec3, vec3 } from 'gl-matrix';
import { ContourTexture } from '../../data/structure/ContourTexture';
import { RegionOfInterest } from '../../data/structure/RegionOfInterest';
import {
    BooleanOperation,
    SDFOperations,
} from '../../util/algorithms/sdf/SDFOperations';
import { View } from '../../view/View';
import { PlaneView } from '../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import { ToolAction } from '../ToolAction';
import { ImageObject } from '~/engine/data/image/ImageObject';

/**
 * {@link DrawActionState} stores the state of the active drawing action.\
 * {@link DrawActionState} is constructed when the user starts drawing and deleted when the drawing finishes.
 */
export abstract class DrawActionState {
    /**
     * An {@link Array} of {@link RegionOfInterest} targets and their {@link SliceSource} targetSpaces.\
     * These are the datamodels that the drawing operation should modify.
     */
    protected readonly _targetStructure: RegionOfInterest;

    protected readonly targetImage: ImageObject;

    protected readonly _erase: boolean = false;

    /**
     * {@link DrawActionState} constructor, which takes the {@link PlaneView} on which the drawing action occurs\
     * and an {@link Array} of {@link targets} that the drawing action should modify.
     * @param view The {@link PlaneView} on which the drawing action occured.
     * @param targets An {@link Array} of {@link targets} that the drawing action should modify.
     */
    public constructor(
        view: PlaneView,
        targetStructure: RegionOfInterest,
        targetImage: ImageObject,
        erase: boolean
    ) {
        this._targetStructure = targetStructure;
        this.targetImage = targetImage;
        this._erase = erase;
    }

    /**
     * Method to get the {@link RegionOfInterest.color color} of the first {@link targets target}.
     * @returns The {@link RegionOfInterest.color color} of the first {@link targets target}.
     */
    protected get roiColor(): ReadonlyVec3 {
        return this._targetStructure.color || vec3.fromValues(0, 1.0, 0);
    }

    public get targetStructure(): RegionOfInterest {
        return this._targetStructure;
    }

    /**
     * Method to apply the drawing.\
     * This renders the drawing on the target structure texture.
     */
    public applyBoolean(): void {
        const drawing = this.contourTexture;
        if (drawing && this._targetStructure.texture) {
            const roiModify = this._targetStructure.startModify(
                this.actionName
            );
            SDFOperations.applyBoolean(
                this._targetStructure.texture,
                drawing,
                this._erase ? BooleanOperation.AND_NOT : BooleanOperation.OR,
                this._targetStructure
            );
            drawing.delete();
            roiModify.finish();
        }
    }

    /**
     * Method to get the {@link ContourTexture} of the drawing.\
     * This is the drawing that is rendered on the {@link targets}.\
     * So it should be the finished 3D {@link ContourTexture}.
     */
    protected abstract get contourTexture(): ContourTexture | undefined;

    /**
     * Method that is called by the {@link DrawAction} when the user moves the mouse.
     * @param coordinates The {@link ReadonlyVec2 client pixel coordinates} of the mouse move event.
     * @param view The {@link View} on which the event occured.
     * @returns An {@link EventHandlerResult} informing how this {@link DrawActionState} reacted to the event and how to proceed.
     */
    public abstract onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: View
    ): EventHandlerResult;

    /**
     * This should deallocate all the memory consumed by WebGL objects.
     * @returns True to indicate that the WebGL memory was released.
     */
    public abstract clearMemory(): void;

    public abstract get actionName(): string;
}

/**
 * {@link DrawAction} is an abstraction, that defines the common interface for all the drawing actions.\
 * {@link DrawAction} is responsible of starting and finishing the {@link DrawActionState}.
 * It has two generic types:
 * - {@link TargetSpace} defines the type of space in which the {@link DrawAction} should operate.
 * - {@link T} defines the exact type of the state that the {@link DrawAction} uses.
 */
export abstract class DrawAction extends ToolAction {
    /**
     * Button that will start the contouring event.
     */
    protected readonly drawButton: number;

    /**
     * Button that will terminate the current contouring event.
     */
    protected readonly cancelButton: number;

    /**
     * {@link ModifierKeys} that need to be active during the {@link onMousePress}-event to start the {@link DrawAction}.
     */
    private readonly modifierKeys: ModifierKeys;

    protected _state?: DrawActionState;

    /**
     * Constructor for the {@link DrawAction}.\
     * All {@link DrawAction DrawActions} are started by pressing the {@link drawButton} with {@link modifierKeys}.\
     * {@link DrawAction} may define a button to cancel the active {@link ToolAction.activeEventState drawActionEvent}.
     * @param drawButton Button that will start the contouring event.
     * @param cancelButton Button that will terminate the current contouring event.
     * @param modifierKeys {@link ModifierKeys} that need to be active during the {@link onMousePress}-event to start the {@link DrawAction}.
     */
    public constructor(
        drawButton: number,
        cancelButton: number,
        modifierKeys: ModifierKeys = ModifierKeys.NONE
    ) {
        super();
        this.drawButton = drawButton;
        this.cancelButton = cancelButton;
        this.modifierKeys = modifierKeys;
    }

    /**
     * Method that is called by the {@link ToolManager} when the user presses the mouse button.\
     * This method checks if the {@link DrawAction} should start and if so, calls {@link startAction} to start the {@link DrawActionState}.
     * @param button Mouse button that triggered the event.
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event.
     * @param coordinates The mouse position in {@link ReadonlyVec2 client pixel coordinates} at the time of the event.
     * @param view The {@link View} on which the event occured.
     * @returns An {@link EventHandlerResult} informing how this {@link DrawAction} reacted to the event and how to proceed.
     */
    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: View
    ): EventHandlerResult {
        if (!this._state) {
            if (view instanceof PlaneView) {
                if (
                    button === this.drawButton &&
                    modifierKeys.equals(this.modifierKeys)
                ) {
                    this._state = this.startAction(view, coordinates);
                    return new EventHandlerResult(
                        EventHandlerResultType.EVENT_HANDLED
                    );
                } else {
                    return new EventHandlerResult(
                        EventHandlerResultType.EVENT_NOT_HANDLED
                    );
                }
            }
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Method that is called by the {@link ToolManager} when the user moves the mouse.\
     * This method calls {@link DrawActionState.onMouseMove} if the {@link DrawAction} is active.
     * @param coordinates The mouse position in {@link ReadonlyVec2 client pixel coordinates} at the time of the event.
     * @param view The {@link View} on which the event occured.
     * @returns An {@link EventHandlerResult} informing how this {@link DrawAction} reacted to the event and how to proceed.\
     * If {@link activeEventState} is undefined, returns {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED}.
     */
    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: View
    ): EventHandlerResult {
        if (this._state) {
            return this._state.onMouseMove(coordinates, buttons, view);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Method that is called by the {@link ToolManager} when the user presses a key.\
     * If the key is "Alt", this method toggles the {@link BooleanOperation} and returns {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_HANDLED EVENT_HANDLED}.\
     * Otherwise this method returns {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED}.
     * @param key The key that triggered the event.
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event.
     * @param _view Optional {@link View} on which the mouse was when the event occured.
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_HANDLED EVENT_HANDLED} if {@link key} was "Alt",\
     * {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} otherwise.
     */
    public onKeyPress(
        key: string,
        _modifierKeys: ModifierKeys,
        _view?: View | undefined
    ): EventHandlerResult {
        if (key === 'Alt') {
            //DrawingTool.toggleErase();
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public abstract startAction(
        view: PlaneView,
        canvasCoordinates: ReadonlyVec2
    ): DrawActionState;

    // Stop the current action and return the result. The result is now returned only to trigger View updates.
    public abstract stopAction(): EventHandlerResult;
}
